import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  AssignmentReturn as AssignmentReturnIcon,
} from '@mui/icons-material';

/** Resumo simplificado do fluxo de saque (para usuários) */
const FluxoSaqueResumo: React.FC = () => (
  <Box>
    <Typography variant="body1" paragraph>
      O saque permite que paróquias solicitem a transferência dos valores dos seus eventos. O processo é seguro e acontece em <strong>duas etapas com confirmação por e-mail</strong>.
    </Typography>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Como funciona para a paróquia
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Cadastre a chave PIX na página de Saque" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Veja os eventos com saldo disponível na aba &quot;Disponíveis&quot;" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Clique em &quot;Solicitar saque&quot; – um código será enviado para seu e-mail" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Digite o código no campo e confirme – o saque será enviado para aprovação" />
      </ListItem>
    </List>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Como funciona para o admin
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Acesse Transações → Solicitações de Saque" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Clique em &quot;Aprovar&quot; – um código será enviado para meusacramento@gmail.com" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Digite o código recebido e confirme – ao aprovar, um e-mail com dados para nota fiscal é enviado" />
      </ListItem>
    </List>

    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      Os códigos são válidos por <strong>10 minutos</strong>. Só é possível um saque por vez por evento.
    </Typography>
  </Box>
);

/** Documentação técnica completa do fluxo de saque */
const FluxoSaqueTecnico: React.FC = () => (
  <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto', whiteSpace: 'pre-wrap', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
{`# Fluxo de Saque - Documentação Técnica

## Modelos
- WithdrawalRequest, WithdrawalVerificationToken, AdminWithdrawalApprovalToken

## Status
pending | processing | completed | rejected | cancelled

## API Paróquia
GET  /api/dashboard/withdrawal-data
PUT  /api/dashboard/withdrawal-data
GET  /api/dashboard/withdrawal/events
POST /api/dashboard/withdrawal/send-token    { eventId, amount }
POST /api/dashboard/withdrawal/request       { eventId, token, amount }
DELETE /api/dashboard/withdrawal/request/:id

## API Admin
GET  /api/admin/withdrawal-requests?status=
POST /api/admin/withdrawal-requests/:id/send-approval-token
PUT  /api/admin/withdrawal-requests/:id/approve   { token }
PUT  /api/admin/withdrawal-requests/:id/reject    { reason? }

## Arquivos
Backend: dashboardController, WithdrawalController, emailService
Frontend: SaquePage.tsx, Transactions.tsx, dashboardService

## Cálculo
Repasse paróquia = valor bruto × (1 - taxa/100)
Taxa padrão: 6,9%
`}
  </Box>
);

/** Resumo multi-organização (para admins) */
const FluxoMultiOrgResumo: React.FC = () => (
  <Box>
    <Typography variant="body1" paragraph>
      Permite que um mesmo <strong>parish_admin</strong> opere várias organizações (CNPJs diferentes)
      com um único login — no estilo Mercado Pago / Shopify. Só funciona se o master liberar a flag.
    </Typography>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
      Como liberar (master)
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Paróquias → Features da organização → Multi-organização" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Ligue o switch do administrador desejado e salve" />
      </ListItem>
    </List>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Como usar (painel da organização)
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Com a flag ativa, aparece o seletor de organização no menu lateral" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Adicionar organização → CNPJ + dados da empresa + conta (sem criar login novo)" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="A org atual permanece ativa; troque pelo seletor quando quiser" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Eventos e saque seguem a organização ativa no JWT" />
      </ListItem>
    </List>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Regras
    </Typography>
    <TableContainer sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Regra</TableCell>
            <TableCell>Comportamento</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            ['Flag desligada', 'Sem botão; API de criar org → 403'],
            ['Flag ligada', 'Pode adicionar orgs extras no painel'],
            ['CNPJ', 'Único na plataforma'],
            ['CPF / e-mail do responsável', 'Pode repetir (mesmo user)'],
            ['Formulário público de cadastro', 'Continua com trava de CPF/e-mail'],
            ['Após criar', 'Mantém a org ativa; toast de confirmação'],
            ['Brand', 'Nova org herda brandKey da ativa (ex.: zuadaria)'],
          ].map(([regra, comportamento]) => (
            <TableRow key={regra}>
              <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{regra}</TableCell>
              <TableCell>{comportamento}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Typography variant="body2" color="text.secondary">
      Em uma frase: <strong>mesmo usuário, várias empresas (CNPJs), troca no painel</strong>; o cadastro
      público continua bloqueando duplicata.
    </Typography>
  </Box>
);

const FluxoMultiOrgTecnico: React.FC = () => (
  <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto', whiteSpace: 'pre-wrap', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
{`# Multi-organização — Documentação Técnica

## Modelo User
- allowAdditionalOrganizations?: boolean (default false)
- parishId → organização ativa (JWT / middleware)
- parishIds: ObjectId[] → todas as orgs do admin
- Bootstrap no login /auth/me: se parishIds vazio e existe parishId → parishIds = [parishId]

## API Master
PUT /api/admin/parish-users/:userId/additional-organizations
  { allowAdditionalOrganizations: boolean }

## API Auth / Painel
POST /api/auth/switch-parish  { parishId }
  → valida parishId ∈ parishIds, atualiza parishId, devolve token + /me

POST /api/dashboard/organizations  (parish_admin + flag)
  Body: parishData + recipientProfile (shape do cadastro público, sem criar User)
  → CNPJ único; parishIds.push(nova); não troca parishId ativo

GET /api/auth/me
  → allowAdditionalOrganizations, organizations: [{ _id, name, slug, cpfCnpj }]

## Arquivos
Backend: User.ts, userParishMembership.ts, authController (switchParish + serialize),
  additionalOrganizationController.ts, ParishController (toggle), adminRoutes, dashboardRoutes
Frontend: Parishes.tsx (flag), MainLayout (switcher), AddOrganizationPage.tsx,
  authSlice (switchParish), dashboardService

## Público
checkDuplicates / cadastro-paroquia: sem mudança (trava permanece)
`}
  </Box>
);

/** Pedido de reembolso via FAQ do evento */
const FluxoReembolsoFaqResumo: React.FC = () => (
  <Box>
    <Typography variant="body1" paragraph>
      Na página pública do evento (FAQ de cancelamento), o participante abre um pedido com motivo +
      e-mail e confirma por código. O pedido aparece no painel do evento em{' '}
      <strong>Atendimento</strong> e também em <strong>Reembolso → Pedidos solicitados</strong> (master).
      Páginas personalizadas não mostram o botão (só o canal do organizador).
    </Typography>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
      Política (elegibilidade)
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Até 7 dias corridos após a compra" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="E faltarem mais de 48 horas para o início do evento" />
      </ListItem>
    </List>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Três caminhos
    </Typography>
    <TableContainer sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Situação</TableCell>
            <TableCell>O que a paróquia faz</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            [
              'Dentro da política + tem saldo no evento',
              'Pode Aprovar na aba Atendimento → estorno no provedor',
            ],
            [
              'Fora da política (passou 7 dias ou < 48h do evento)',
              'Não aprova pelo fluxo automático; pode Recusar (ou master trata)',
            ],
            [
              'Dentro da política, mas sem saldo / saque em andamento',
              'Não aprova sozinha; suporte trata (saldo já saiu ou saque pendente)',
            ],
          ].map(([situacao, acao]) => (
            <TableRow key={situacao}>
              <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{situacao}</TableCell>
              <TableCell>{acao}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
      Avisos para o suporte (meusacramento@gmail.com)
    </Typography>
    <List dense disablePadding>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Abertura do pedido → e-mail para suporte + organizador" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Paróquia recusa → e-mail imediato ao suporte" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Paróquia aprova e estorna → e-mail de confirmação ao suporte" />
      </ListItem>
      <ListItem sx={{ py: 0 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Sem resposta da paróquia por 2+ dias → alerta diário (cron 10h BRT)" />
      </ListItem>
    </List>

    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      Observação: o participante ainda vê a mesma mensagem de sucesso ao abrir o pedido (sem texto
      diferenciado por classificação). A classificação aparece no e-mail interno e na aba Atendimento.
    </Typography>
  </Box>
);

const FluxoReembolsoFaqTecnico: React.FC = () => (
  <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto', whiteSpace: 'pre-wrap', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
{`# Reembolso FAQ — Documentação Técnica

## Modelo
CancellationTicket
- status: pending_verification | opened | handled | expired
- parishDecision: pending | approved | rejected
- eligibilityBucket: eligible_with_balance | out_of_policy |
  insufficient_balance | withdrawal_in_progress | registration_not_found
- overdueNotifiedAt (preenchido 1x pelo cron)

## Público
POST /api/public/cancellation-tickets/request  { email, reason, eventId|eventSlug }
POST /api/public/cancellation-tickets/confirm  { email, token, ticketId? }

## Painel paróquia
GET  /api/dashboard/events/:eventId/cancellation-tickets
POST /api/dashboard/events/:eventId/cancellation-tickets/:id/approve
POST /api/dashboard/events/:eventId/cancellation-tickets/:id/reject

## Master
GET  /api/admin/cancellation-tickets?status=opened|handled|all
POST /api/admin/cancellation-tickets/:id/handle

## Cron atraso (baixo custo)
jobs/cancellationTicketOverdueMonitor.ts
- 1x/dia 10:00 America/Sao_Paulo
- query: opened + parishDecision pending + overdueNotifiedAt null + verifiedAt <= 2 dias
- claim atômico antes do e-mail

## Arquivos
Backend: CancellationTicket.ts, cancellationTicketController.ts,
  cancellationTicketEligibility.ts, refundWithdrawalGuard.ts, registrationRefundService.ts
Frontend: CancellationTicketDialog, EventWorkspaceAtendimentoTab, Refunds.tsx
`}
  </Box>
);

interface FlowItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  chip?: string;
  resumo: React.ReactNode;
  tecnico: React.ReactNode;
}

const FLUXOS: FlowItem[] = [
  {
    id: 'saque',
    title: 'Fluxo de Saque',
    icon: <AccountBalanceWalletIcon />,
    chip: '2 etapas',
    resumo: <FluxoSaqueResumo />,
    tecnico: <FluxoSaqueTecnico />,
  },
  {
    id: 'multi-organizacao',
    title: 'Multi-organização',
    icon: <BusinessIcon />,
    chip: 'flag no master',
    resumo: <FluxoMultiOrgResumo />,
    tecnico: <FluxoMultiOrgTecnico />,
  },
  {
    id: 'reembolso-faq',
    title: 'Reembolso (FAQ do evento)',
    icon: <AssignmentReturnIcon />,
    chip: 'Atendimento',
    resumo: <FluxoReembolsoFaqResumo />,
    tecnico: <FluxoReembolsoFaqTecnico />,
  },
];

const CATEGORIAS = [
  { id: 'fluxos', label: 'Fluxos', flows: FLUXOS },
];

const DocumentationPage: React.FC = () => {
  const location = useLocation();
  const [expandedFlow, setExpandedFlow] = useState<string | null>(null);
  const [showTecnico, setShowTecnico] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fromHash = (location.hash || '').replace(/^#/, '').trim();
    const fromQuery = new URLSearchParams(location.search).get('flow') || '';
    const target = fromHash || fromQuery;
    if (!target) return;
    const exists = FLUXOS.some((f) => f.id === target);
    if (exists) {
      setExpandedFlow(target);
      // scroll after paint
      requestAnimationFrame(() => {
        document.getElementById(`doc-flow-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash, location.search]);

  const toggleTecnico = (flowId: string) => {
    setShowTecnico((prev) => ({ ...prev, [flowId]: !prev[flowId] }));
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom display="flex" alignItems="center" gap={1}>
          <MenuBookIcon />
          Documentação
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Guias e fluxos do sistema, organizados por categoria. Cada fluxo tem uma visão simplificada e uma documentação técnica completa.
        </Typography>
      </Box>

      {CATEGORIAS.map((cat) => (
        <Card key={cat.id} variant="outlined" sx={{ mb: 3 }}>
          <CardHeader title={cat.label} />
          <CardContent>
            {cat.flows.map((flow) => (
              <Accordion
                key={flow.id}
                id={`doc-flow-${flow.id}`}
                expanded={expandedFlow === flow.id}
                onChange={(_, isExpanded) => setExpandedFlow(isExpanded ? flow.id : null)}
                sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    {flow.icon}
                    <Typography fontWeight="medium">{flow.title}</Typography>
                    {flow.chip ? <Chip label={flow.chip} size="small" variant="outlined" /> : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                      Visão geral (para todos)
                    </Typography>
                    {flow.resumo}
                  </Paper>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CodeIcon />}
                    onClick={() => toggleTecnico(flow.id)}
                    sx={{ mb: showTecnico[flow.id] ? 2 : 0 }}
                  >
                    {showTecnico[flow.id] ? 'Ocultar documentação técnica' : 'Ver documentação técnica completa'}
                  </Button>

                  {showTecnico[flow.id] && (
                    <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
                      <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 2, py: 1 }}>
                        <Typography variant="subtitle2">Documentação para programadores</Typography>
                      </Box>
                      <Box sx={{ p: 0 }}>
                        {flow.tecnico}
                      </Box>
                    </Paper>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      ))}

      <Typography variant="caption" color="text.secondary">
        Novos fluxos serão adicionados aqui conforme forem documentados.
      </Typography>
    </Box>
  );
};

export default DocumentationPage;
